import React, { useState } from "react";
import { supabase } from "../../../supabaseClient";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import { computeInvoice, POSITIONING_FEE, EXTRA_PRICES, isExtraWaived } from "../../../components/invoiceCalc";
import { TENT_LABELS } from "../../../components/Tentvisuals";
import "./Step5.css";

export default function Step5Confirm({ vendor, event, app, formData, onComplete }) {
  const [submitting, setSubmitting] = useState(false);

  if (!vendor || !app) {
    return <p className="s5-error">Could not load your application. Please contact support.</p>;
  }

  const isSponsored = app.tent_type === "sponsored";
  const isBizBoost  = app.tent_type === "biz_boost";
  const isITC       = app.tent_type === "ITC";
  const isWfto      = app.tent_type === "WFTO";
  

  const hasCustomPrice = app.custom_base_price != null && app.custom_base_price > 0;
  const supportsCustomPrice = isSponsored || isITC;

  // Custom-price space with no price set yet — show a holding message
  if (supportsCustomPrice && !hasCustomPrice) {
    return (
      <div className="s5-container">
        <h2 className="s5-title">Invoice Pending</h2>
        <p className="s5-subtitle">
          {isBizBoost
            ? "Your Biz Boost Program pricing is being confirmed by the BizBaz team."
            : isSponsored
            ? "Your Sponsored space pricing is being confirmed by the BizBaz team."
            : "Your ITC space pricing is being confirmed by the BizBaz team."}
          {" "}You'll be notified once your invoice is ready — please check back shortly.
        </p>
      </div>
    );
  }

  // Single source of truth for all pricing math — same calculator used by
  // VendorEventWorkspace's InvoiceTab, so the two never drift apart.
  const { basePrice, positioningFee, subtotal, vatAmount, total, depositRequired } = computeInvoice({
    tentType: app.tent_type,
    customBasePrice: app.custom_base_price,
    extras: formData.extras,
    hasChosenSpot: !!formData.spot,
  });

  const balance = total - depositRequired;

  // ── Confirm ──
  const handleConfirm = async () => {
    if (!window.confirm("Confirm and complete onboarding?")) return;
    setSubmitting(true);

    try {
      const { error: updateErr } = await supabase
        .from("vendor_applications")
        .update({
          onboarding_completed: true,
          extras:               formData.extras || {},
          chosen_spot:          formData.spot   || null,
          invoice_total:        total,
          deposit_required:     depositRequired,
        })
        .eq("id", app.id);

      if (updateErr) throw updateErr;

      console.log("✓ onboarding_completed set to true for application:", app.id);

      // Try PDF upload (non-blocking)
      try {
        const invoiceEl = document.querySelector(".s5-invoice-box");
        if (invoiceEl) {
          const canvas = await html2canvas(invoiceEl, { scale: 2, useCORS: true });
          const imgData = canvas.toDataURL("image/png");
          const pdf = new jsPDF("p", "mm", "a4");
          const w = 210;
          const h = (canvas.height * w) / canvas.width;
          pdf.addImage(imgData, "PNG", 0, 0, w, h);
          const blob = pdf.output("blob");
          const safeBrand = (vendor.brand_name || "vendor")
            .replace(/\s+/g, "_")
            .toLowerCase()
            .replace(/[^a-z0-9_-]/g, "");

          await supabase.storage
            .from("Vendor_onboarding_forms")
            .upload(`${safeBrand}/invoice.pdf`, blob, {
              upsert: true,
              contentType: "application/pdf",
            });
          console.log("✓ Invoice PDF uploaded");
        }
      } catch (pdfErr) {
        console.warn("PDF upload failed (non-fatal):", pdfErr);
      }

      onComplete?.();

      alert("Onboarding complete! You'll be redirected to your dashboard.");
      window.location.href = `/vendor/events/${event?.event_key || event?.id}`;

    } catch (err) {
      console.error("Confirm error:", err);
      alert("Something went wrong: " + err.message);
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ──
  return (
    <div className="s5-container">
      <h2 className="s5-title">Invoice Summary</h2>
      <p className="s5-subtitle">Review your order before completing onboarding.</p>

      <div className="s5-invoice-box">
        <div className="s5-inv-brand">
          <div>
            <h3 className="s5-inv-brand-name">{vendor.brand_name}</h3>
            <p className="s5-inv-brand-meta">{vendor.email}</p>
          </div>
          <div className="s5-inv-event-tag">{event?.name}</div>
        </div>

        <div className="s5-inv-divider" />

        <div className="s5-inv-lines">
          <div className="s5-inv-row">
            <span>
              Base Tent ({TENT_LABELS[app.tent_type] || app.tent_type})
              {isBizBoost && (
                <span style={{ marginLeft: 8, fontSize: "0.75rem", color: "#6b7280", fontStyle: "italic" }}>
                  — Biz Boost Program
                </span>
              )}
              {isSponsored && (
                <span style={{ marginLeft: 8, fontSize: "0.75rem", color: "#6b7280", fontStyle: "italic" }}>
                  — Discounted / Sponsored
                </span>
              )}
              {isITC && (
                <span style={{ marginLeft: 8, fontSize: "0.75rem", color: "#6b7280", fontStyle: "italic" }}>
                  — ITC Partner
                </span>
              )}
              {isWfto && (
                <span style={{ marginLeft: 8, fontSize: "0.75rem", color: "#6b7280", fontStyle: "italic" }}>
                  — WFTO Partner
                </span>
              )}
            </span>
            <span>KES {basePrice.toLocaleString()}</span>
          </div>

          {Object.entries(formData.extras ?? {}).map(([key, qty]) => {
            if (!qty) return null;
            const waived = isExtraWaived(key, app.tent_type);
            const originalLineTotal = Math.round((EXTRA_PRICES[key] || 0) * qty);
            const lineTotal = waived ? 0 : originalLineTotal;
            return (
              <div className="s5-inv-row" key={key}>
                <span>
                  {key} × {qty}
                  {waived && (
                    <span style={{ marginLeft: 8, fontSize: "0.75rem", color: "#6b7280", fontStyle: "italic" }}>
                      — Waived
                    </span>
                  )}
                </span>
                <span>
                  {waived ? (
                    <>
                      <span style={{ textDecoration: "line-through", color: "#9ca3af", marginRight: 6 }}>
                        KES {originalLineTotal.toLocaleString()}
                      </span>
                      KES 0
                    </>
                  ) : (
                    `KES ${lineTotal.toLocaleString()}`
                  )}
                </span>
              </div>
            );
          })}

          {positioningFee > 0 && (
            <div className="s5-inv-row">
              <span>Self-Selected Spot</span>
              <span>KES {POSITIONING_FEE.toLocaleString()}</span>
            </div>
          )}
        </div>

        <div className="s5-inv-divider" />

        <div className="s5-inv-row">
          <span>Subtotal</span>
          <span>KES {subtotal.toLocaleString()}</span>
        </div>

        <div className="s5-inv-row s5-inv-row--muted">
          <span>VAT (16%)</span>
          <span>KES {Math.round(vatAmount).toLocaleString()}</span>
        </div>

        <div className="s5-inv-divider" />

        <div className="s5-inv-total">
          <span>Total</span>
          <span>KES {total.toLocaleString()}</span>
        </div>

        <div className="s5-inv-breakdown">
          <div className="s5-inv-break-item">
            <span>Deposit (50%)</span>
            <strong>KES {depositRequired.toLocaleString()}</strong>
            <small>Due 48 hours after acceptance.</small>
          </div>
          <div className="s5-inv-break-sep">+</div>
          <div className="s5-inv-break-item">
            <span>Balance</span>
            <strong>KES {balance.toLocaleString()}</strong>
            <small>Due before event date.</small>
          </div>
        </div>
      </div>

      <div className="s5-payment-section" style={{ display: "none" }}>
        <h4 className="s5-payment-title">Payment Methods</h4>
        <div className="s5-payment-grid">
          <div className="s5-pay-card">
            <p className="s5-pay-card-title">🏦 Bank Transfer</p>
            <div className="s5-pay-row"><span>Account Name</span><strong>Biz Baz Events Limited</strong></div>
            <div className="s5-pay-row"><span>Account No.</span><strong>177 027 632 3286</strong></div>
            <div className="s5-pay-row"><span>Bank</span><strong>Equity Bank</strong></div>
            <div className="s5-pay-row"><span>Branch</span><strong>Parliament Road</strong></div>
            <div className="s5-pay-row"><span>Branch Code</span><strong>177</strong></div>
          </div>
          <div className="s5-pay-card">
            <p className="s5-pay-card-title">📱 M-Pesa Paybill</p>
            <div className="s5-pay-row"><span>Paybill</span><strong>247 247</strong></div>
            <div className="s5-pay-row"><span>Account No.</span><strong>826 201</strong></div>
            <div className="s5-pay-row"><span>Account Name</span><strong>Biz Baz Events Limited</strong></div>
          </div>
        </div>
      </div>

      <button className="s5-confirm-btn" onClick={handleConfirm} disabled={submitting}>
        {submitting ? "Processing…" : "Confirm & Go to Dashboard →"}
      </button>
    </div>
  );
}